<template>
  <transition name="app-modal-fade">
    <div
      v-if="visible"
      class="app-confirm-backdrop"
      @mousedown.self="onBackdropMouseDown"
      @mouseup.self="onBackdropMouseUp">
      <div class="app-confirm" role="dialog" aria-modal="true" :aria-label="title || 'Confirmación'">
        <header class="app-confirm__header">
          <slot name="header">
            <h3 class="app-confirm__title">
              <span v-if="icon" class="material-icons app-confirm__icon">{{ icon }}</span>
              <span>{{ title }}</span>
            </h3>
          </slot>
        </header>

        <main class="app-confirm__body">
          <slot>
            <div
              class="app-confirm__content"
              :class="{ 'app-confirm__content--danger': messageVariant === 'danger' }"
              v-html="message"
            ></div>
          </slot>
        </main>

        <footer class="app-confirm__footer">
          <AppButton
            v-if="showCancelButton"
            :variant="cancelButton.variant"
            :outline="cancelButton.outline"
            @click="cancel">
            {{ cancelButton.text }}
          </AppButton>
          <AppButton
            :variant="confirmButton.variant"
            :outline="confirmButton.outline"
            :disabled="confirming"
            @click="confirm">
            <span v-if="confirming" class="app-confirm__spinner"></span>
            {{ confirmButton.text }}
          </AppButton>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import AppButton from './AppButton.vue'

const defaultParams = {
  title: 'Confirmar acción',
  message: '',
  icon: '',
  messageVariant: '',
  showCancelButton: true,
  confirmButton: {
    text: 'Confirmar',
    variant: 'primary',
    outline: false
  },
  cancelButton: {
    text: 'Cancelar',
    variant: 'secondary',
    outline: true
  }
}

export default {
  name: 'AppConfirmModal',
  components: {
    AppButton
  },
  data () {
    return {
      visible: false,
      title: defaultParams.title,
      message: defaultParams.message,
      icon: defaultParams.icon,
      messageVariant: defaultParams.messageVariant,
      showCancelButton: defaultParams.showCancelButton,
      confirmButton: { ...defaultParams.confirmButton },
      cancelButton: { ...defaultParams.cancelButton },
      resolvePromise: null,
      confirming: false,
      mouseDownOnBackdrop: false
    }
  },
  watch: {
    visible (isOpen) {
      if (isOpen) {
        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', this.handleEsc)
      } else {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', this.handleEsc)
      }
    }
  },
  methods: {
    open (message = '', params = {}) {
      this.message = message
      this.title = params.title || defaultParams.title
      this.icon = params.icon || defaultParams.icon
      this.messageVariant = params.messageVariant || defaultParams.messageVariant
      this.showCancelButton = params.showCancelButton !== undefined
        ? params.showCancelButton
        : defaultParams.showCancelButton
      this.confirmButton = {
        ...defaultParams.confirmButton,
        ...(params.confirmButton || {})
      }
      this.cancelButton = {
        ...defaultParams.cancelButton,
        ...(params.cancelButton || {})
      }
      this.confirming = false
      this.visible = true

      return new Promise(resolve => {
        this.resolvePromise = resolve
      })
    },
    confirm () {
      this.confirming = true
      this.finish(true)
    },
    cancel () {
      this.finish(false)
    },
    finish (result) {
      this.visible = false
      this.confirming = false
      if (this.resolvePromise) {
        this.resolvePromise(result)
        this.resolvePromise = null
      }
    },
    onBackdropMouseDown () {
      this.mouseDownOnBackdrop = true
    },
    onBackdropMouseUp () {
      if (this.mouseDownOnBackdrop) {
        this.cancel()
      }
      this.mouseDownOnBackdrop = false
    },
    handleEsc (e) {
      if (e.key === 'Escape') {
        this.cancel()
      }
    }
  },
  destroyed () {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', this.handleEsc)
  }
}
</script>

<style scoped>
.app-confirm-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1060;
}

.app-confirm {
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  margin: 1rem;
}

.app-confirm__header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--app-border-color, #e5e7eb);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.app-confirm__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.app-confirm__icon {
  font-size: 1.5rem;
}

.app-confirm__body {
  padding: 1.5rem;
  overflow-y: auto;
}

.app-confirm__content {
  color: var(--app-text-color, #4b5563);
  font-size: 0.9375rem;
  line-height: 1.5;
}

.app-confirm__content--danger {
  color: var(--status-rejected, #ef4444);
  font-weight: 600;
}

.app-confirm__footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--app-border-color, #e5e7eb);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.app-confirm__spinner {
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  margin-right: 0.375rem;
  vertical-align: middle;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: app-confirm-spin 0.6s linear infinite;
}

@keyframes app-confirm-spin {
  to {
    transform: rotate(360deg);
  }
}

.app-modal-fade-enter-active,
.app-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.app-modal-fade-enter,
.app-modal-fade-leave-to {
  opacity: 0;
}

.app-modal-fade-enter-active .app-confirm {
  animation: modal-pop 0.2s ease-out;
}

@keyframes modal-pop {
  0% {
    transform: scale(0.95);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
